import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';

function fmtShort(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000)    return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
}

function fmtDate(str) {
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Map app_type keys → display labels
const APP_TYPE_LABELS = {
  moebius_replacement: 'Moebius Replacement',
  moebius_bleed:       'Moebius Bleed',
  moebius_comparison:  'emew vs Moebius',
};

const CATEGORY_LABELS = {
  labour:      'Labour',
  energy:      'Energy',
  reagents:    'Reagents',
  maintenance: 'Maintenance',
  financing:   'Financing',
  revenue:     'Add. Revenue',
};

// Returns { label, saving } for the category with the highest annual saving
function getKeySavingCategory(results) {
  if (!results || !results.competitor_by_category) return null;
  const cats = ['labour', 'energy', 'reagents', 'maintenance', 'financing', 'revenue'];
  let best = null, bestVal = 0;
  for (const cat of cats) {
    const comp = results.competitor_by_category[cat] || 0;
    const emew = results.emew_by_category[cat]       || 0;
    const saving = cat === 'revenue' ? comp + emew : comp - emew;
    if (saving > bestVal) { bestVal = saving; best = cat; }
  }
  return best ? { label: CATEGORY_LABELS[best], saving: bestVal } : null;
}

export default function Home() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    api.get('/assessment')
      .then(res => setAssessments(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ padding: '1.5rem 0' }}>

      {/* Hero */}
      <div className="card" style={{ padding: '2rem 2.5rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ marginBottom: '0.375rem' }}>
            <span style={{ color: 'var(--color-green)' }}>e</span>mew AIVA
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', maxWidth: 460 }}>
            AI Value Assessment — techno-economic modelling across emew application types, generating a branded customer-facing PDF.
          </p>
        </div>
        <Link to="/assessment/new" className="btn btn-primary" style={{ fontSize: '0.9375rem', padding: '0.625rem 1.5rem' }}>
          + New Assessment
        </Link>
      </div>

      {/* List header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1rem' }}>Recent Assessments</h2>
        {assessments.length > 0 && (
          <Link to="/assessment/new" className="btn btn-outline btn-sm">+ New</Link>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <span className="spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
        </div>
      ) : assessments.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <p style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📋</p>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.25rem' }}>No assessments yet. Select an application type to generate your first value assessment.</p>
          <Link to="/assessment/new" className="btn btn-primary">+ New Assessment</Link>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#EBF0FA' }}>
                <th style={{ padding: '0.65rem 1rem', textAlign: 'left',  fontWeight: 700, color: 'var(--color-dark-blue)' }}>Customer</th>
                <th style={{ padding: '0.65rem 1rem', textAlign: 'left',  fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>Application Type</th>
                <th style={{ padding: '0.65rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>Annual Saving</th>
                <th style={{ padding: '0.65rem 1rem', textAlign: 'left',  fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>Key Saving Category</th>
                <th style={{ padding: '0.65rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((a, i) => {
                const results = typeof a.results === 'string' ? (() => { try { return JSON.parse(a.results); } catch { return null; } })() : a.results;
                const isV2    = results?.engine === 'v2';

                // Application type label
                const appType    = a.app_type || results?.inputs?.app_type;
                const appLabel   = APP_TYPE_LABELS[appType] || a.application || '—';

                // Annual saving: v2 uses annual_saving, v1 uses annual_value_usd
                const annualSaving = isV2
                  ? results?.annual_saving
                  : results?.annual_value_usd;

                // Key saving category (v2 only)
                const keyCat = isV2 ? getKeySavingCategory(results) : null;

                return (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/assessment/${a.id}`)}
                    style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border)', background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}
                  >
                    {/* Customer */}
                    <td style={{ padding: '0.55rem 1rem' }}>
                      <div style={{ fontWeight: 600 }}>{a.customer_name}</div>
                      {(a.contact_person || a.country) && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                          {[a.contact_person, a.country].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>

                    {/* Application type */}
                    <td style={{ padding: '0.55rem 1rem' }}>
                      <span style={{
                        display: 'inline-block',
                        fontSize: '0.75rem', fontWeight: 600,
                        background: appType === 'moebius_bleed' ? '#EBF5FB' : '#EBF0FA',
                        color:      appType === 'moebius_bleed' ? '#1A6FA8' : 'var(--color-blue)',
                        borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap',
                      }}>
                        {appLabel}
                      </span>
                    </td>

                    {/* Annual saving */}
                    <td style={{ padding: '0.55rem 1rem', textAlign: 'right', fontWeight: 700, color: annualSaving > 0 ? 'var(--color-green)' : 'var(--color-text-muted)' }}>
                      {annualSaving != null ? fmtShort(annualSaving) + '/yr' : '—'}
                    </td>

                    {/* Key saving category */}
                    <td style={{ padding: '0.55rem 1rem' }}>
                      {keyCat ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, background: '#F0FDF4', color: '#166534', borderRadius: 4, padding: '2px 7px' }}>
                            {keyCat.label}
                          </span>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                            {fmtShort(keyCat.saving)}/yr
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>

                    {/* Created date */}
                    <td style={{ padding: '0.55rem 1rem', textAlign: 'right', color: 'var(--color-text-muted)', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                      {fmtDate(a.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
